/**
 * Lock routines (ท่าล็อกเป้า แบบยาว, duel v3 P3): a lock form told as a long, timed routine of beats, the user's "dance that
 * locks the foe for seconds" (research docs/research/DUEL_LOCK_FORMS.md; ideas only, our own moves and choreography).
 *
 * A routine runs once its move connects in full on an OPENED foe (systems/duelLockOpen.ts): both fighters are held for `len`
 * seconds, nothing breaks it once it is sealed. Each beat says what the caster does (`pose`, the animator's key; the client
 * plays a placeholder swing until it exists), how many blows land in it and what share of the routine's damage they carry,
 * how the held foe reacts (`foe`, a receive beat of data/lockForms.ts), and cues for FX, sound and the camera. The first
 * `catch` seconds are the catch: the only moment the held foe may break out (ดิ้นหลุด, once a round, guard + dodge on the
 * white flash).
 *
 * A move with a routine keeps its own wind-up (data/lockForms.ts WINDUP_DATA) and its first strikes; the routine's blows come
 * on top, at the beats. Data only: no DOM, no timers.
 */
import type { FormBeat, FormBeatKind, LockFormDef } from './lockForms';
import { longMovesOf } from './longMoves';
import type { LongBeat, LongCamCue, LongFoeKind, LongMoveDef } from './longMoves/types';

/**
 * The routines are long moves of kind `lock` now (data/longMoves/<art>.ts, one shape for routines and ท่าใหญ่): these names
 * stay as the adapter, so the lock gate and its tests read them as before.
 */
export type LockCamCue = LongCamCue;
export type LockBeat = LongBeat;
export type LockRoutineDef = LongMoveDef & { readonly kind: 'lock'; readonly catch: number };

/** The moves that run a routine (others keep the short form of data/lockForms.ts): a view over the lock long moves. */
export const LOCK_ROUTINES: Readonly<Record<string, LockRoutineDef>> = Object.fromEntries(
  longMovesOf('lock').map((d) => [d.skillId, d as LockRoutineDef]),
);

/** A long move's foe reaction as a lock form's receive beat (the new kinds draw as their nearest old one). */
const RECEIVE: Record<LongFoeKind, FormBeatKind> = {
  hit: 'hit',
  spin: 'spin',
  knock: 'knock',
  kneel: 'kneel',
  fall: 'fall',
  recover: 'recover',
  lift: 'hit',
  pull: 'hit',
  downHit: 'fall',
  prone: 'fall',
};

/** The routine as a paired form (systems/duelLock.ts): its receive beats in shares of the length. */
export function routineForm(r: LongMoveDef): LockFormDef {
  const receive: FormBeat[] = r.beats.map((b, i) => ({
    from: b.at / r.len,
    to: (r.beats[i + 1]?.at ?? r.len) / r.len,
    kind: RECEIVE[b.foe],
    ...(b.dist ? { dist: b.dist } : {}),
  }));
  return { lunge: r.lunge, receive };
}

const FORMS = new Map<string, LockFormDef>();
/** The routine of a move (null: a short form, or no lock move), with its paired form. */
export function lockRoutineOf(skillId: string): { r: LockRoutineDef; form: LockFormDef } | null {
  const r = LOCK_ROUTINES[skillId];
  if (!r) return null;
  let f = FORMS.get(skillId);
  if (!f) FORMS.set(skillId, (f = routineForm(r)));
  return { r, form: f };
}

/** The beat a routine is in at `t` seconds (index), and how far through it (0-1). */
export function beatAt(r: Pick<LongMoveDef, 'beats' | 'len'>, t: number): { i: number; k: number } {
  let i = 0;
  while (i + 1 < r.beats.length && t >= r.beats[i + 1]!.at) i++;
  const from = r.beats[i]!.at;
  const to = r.beats[i + 1]?.at ?? r.len;
  return { i, k: Math.max(0, Math.min(1, (t - from) / Math.max(1e-6, to - from))) };
}

/**
 * The bout's lock numbers (P3; ns-balance to tune): `escapeGp` guard points an escape costs (once a round), `guardedStop` the
 * caster's stop when its lock move is guarded (the shove), `escapeStop` the caster's stop when the foe breaks out of the catch,
 * `escapeDist` metres the escaper steps back.
 */
export const DUEL_LOCK_OPEN = {
  escapeGp: 50,
  guardedStop: 0.45,
  escapeStop: 0.4,
  escapeDist: 2.2,
  /** The caster's stop when its lock move meets a red (armoured) body and whiffs (DUEL-ARMOR). */
  redStop: 0.6,
  /** Seconds the guard key may lead the dodge (or the dodge the guard) and still count as one press of the two. */
  pairWindow: 0.25,
} as const;
